import {
  HarshEventReportApiFactory,
  ApiAppHarsheventreportGetharshaccelerationeventsPostRequest,
  ApiAppHarsheventreportGetfiltereddriverharsheventssummaryPostRequest,
  HarshAccelerationEventsDto,
  HarshBreakingEventsDto,
  HarshStopEventsDto,
  HarshTurnEventsDto,
  HarshSpeedingEventsDto,
  SpeedingDto,
  DriverPerformaceDto,
  HarshEventsBreakdownInfoDto,
  HarshEventDailyLogDto,
  DriverHarshEventsTotalDtoPagedResultDto,
  PeriodType,
} from "../../../common/data/service-proxies";
import { useApiRequest } from "./useApiRequest";
import type { BaseHookReturn } from "./useApiRequest";

export interface HarshDrivingReportsHookParams {
  fromDate: Date;
  toDate: Date;
  periodType?: PeriodType;
}

export interface HarshDrivingReportsFilteredSummaryHookParams
  extends HarshDrivingReportsHookParams {
  skipCount?: number;
  maxResultCount?: number;
}

function toCommonRequest(
  params: HarshDrivingReportsHookParams,
): ApiAppHarsheventreportGetharshaccelerationeventsPostRequest {
  return {
    fromDate: params.fromDate.toISOString(),
    toDate: params.toDate.toISOString(),
    periodType: params.periodType,
  };
}

/** GetHarshStopEvents */
export const useHarshStopEvents = (
  params: HarshDrivingReportsHookParams,
): BaseHookReturn<HarshStopEventsDto> => {
  return useApiRequest<HarshStopEventsDto>(
    [params.fromDate, params.toDate, params.periodType],
    async (axios, baseURL) => {
      const request = toCommonRequest(params);
      const response = await HarshEventReportApiFactory(
        undefined,
        baseURL,
        axios,
      ).apiAppHarsheventreportGetharshstopeventsPost(request);
      return response.data;
    },
    { logLabel: "useHarshStopEvents" },
  );
};

/** GetHarshAccelerationEvents */
export const useHarshAccelerationEvents = (
  params: HarshDrivingReportsHookParams,
): BaseHookReturn<HarshAccelerationEventsDto> => {
  return useApiRequest<HarshAccelerationEventsDto>(
    [params.fromDate, params.toDate, params.periodType],
    async (axios, baseURL) => {
      const request = toCommonRequest(params);
      const response = await HarshEventReportApiFactory(
        undefined,
        baseURL,
        axios,
      ).apiAppHarsheventreportGetharshaccelerationeventsPost(request);
      return response.data;
    },
    { logLabel: "useHarshAccelerationEvents" },
  );
};

/** GetHarshBreakingEvents */
export const useHarshBreakingEvents = (
  params: HarshDrivingReportsHookParams,
): BaseHookReturn<HarshBreakingEventsDto> => {
  return useApiRequest<HarshBreakingEventsDto>(
    [params.fromDate, params.toDate, params.periodType],
    async (axios, baseURL) => {
      const request = toCommonRequest(params);
      const response = await HarshEventReportApiFactory(
        undefined,
        baseURL,
        axios,
      ).apiAppHarsheventreportGetharshbreakingeventsPost(request);
      return response.data;
    },
    { logLabel: "useHarshBreakingEvents" },
  );
};

/** GetHarshTurnEvents */
export const useHarshTurnEvents = (
  params: HarshDrivingReportsHookParams,
): BaseHookReturn<HarshTurnEventsDto> => {
  return useApiRequest<HarshTurnEventsDto>(
    [params.fromDate, params.toDate, params.periodType],
    async (axios, baseURL) => {
      const request = toCommonRequest(params);
      const response = await HarshEventReportApiFactory(
        undefined,
        baseURL,
        axios,
      ).apiAppHarsheventreportGetharshturneventsPost(request);
      return response.data;
    },
    { logLabel: "useHarshTurnEvents" },
  );
};

/** GetHarshSpeedingEvents */
export const useHarshSpeedingEvents = (
  params: HarshDrivingReportsHookParams,
): BaseHookReturn<HarshSpeedingEventsDto> => {
  return useApiRequest<HarshSpeedingEventsDto>(
    [params.fromDate, params.toDate, params.periodType],
    async (axios, baseURL) => {
      const request = toCommonRequest(params);
      const response = await HarshEventReportApiFactory(
        undefined,
        baseURL,
        axios,
      ).apiAppHarsheventreportGetharshspeedingeventsPost(request);
      return response.data;
    },
    { logLabel: "useHarshSpeedingEvents" },
  );
};

/** GetAvgSpeeding */
export const useAvgSpeeding = (
  params: HarshDrivingReportsHookParams,
): BaseHookReturn<SpeedingDto> => {
  return useApiRequest<SpeedingDto>(
    [params.fromDate, params.toDate, params.periodType],
    async (axios, baseURL) => {
      const request = toCommonRequest(params);
      const response = await HarshEventReportApiFactory(
        undefined,
        baseURL,
        axios,
      ).apiAppHarsheventreportGetavgspeedingPost(request);
      return response.data;
    },
    { logLabel: "useAvgSpeeding" },
  );
};

/** GetDriversWithBestPerformance */
export const useDriversWithBestPerformance = (
  params: HarshDrivingReportsHookParams,
): BaseHookReturn<DriverPerformaceDto> => {
  return useApiRequest<DriverPerformaceDto>(
    [params.fromDate, params.toDate, params.periodType],
    async (axios, baseURL) => {
      const request = toCommonRequest(params);
      const response = await HarshEventReportApiFactory(
        undefined,
        baseURL,
        axios,
      ).apiAppHarsheventreportGetdriverswithbestperformancePost(request);
      return response.data;
    },
    { logLabel: "useDriversWithBestPerformance" },
  );
};

/** GetHarshEventsBreakdownInformation */
export const useHarshEventsBreakdownInformation = (
  params: HarshDrivingReportsHookParams,
): BaseHookReturn<HarshEventsBreakdownInfoDto> => {
  return useApiRequest<HarshEventsBreakdownInfoDto>(
    [params.fromDate, params.toDate, params.periodType],
    async (axios, baseURL) => {
      const request = toCommonRequest(params);
      const response = await HarshEventReportApiFactory(
        undefined,
        baseURL,
        axios,
      ).apiAppHarsheventreportGetharsheventsbreakdowninformationPost(request);
      return response.data;
    },
    { logLabel: "useHarshEventsBreakdownInformation" },
  );
};

/** GetDriversWithWorstPerformance */
export const useDriversWithWorstPerformance = (
  params: HarshDrivingReportsHookParams,
): BaseHookReturn<DriverPerformaceDto> => {
  return useApiRequest<DriverPerformaceDto>(
    [params.fromDate, params.toDate, params.periodType],
    async (axios, baseURL) => {
      const request = toCommonRequest(params);
      const response = await HarshEventReportApiFactory(
        undefined,
        baseURL,
        axios,
      ).apiAppHarsheventreportGetdriverswithworstperformancePost(request);
      return response.data;
    },
    { logLabel: "useDriversWithWorstPerformance" },
  );
};

/** GetHarshEventDailyLogs */
export const useHarshEventDailyLogs = (
  params: HarshDrivingReportsHookParams,
): BaseHookReturn<HarshEventDailyLogDto> => {
  return useApiRequest<HarshEventDailyLogDto>(
    [params.fromDate, params.toDate, params.periodType],
    async (axios, baseURL) => {
      const request = toCommonRequest(params);
      const response = await HarshEventReportApiFactory(
        undefined,
        baseURL,
        axios,
      ).apiAppHarsheventreportGetharsheventdailylogsPost(request);
      return response.data;
    },
    { logLabel: "useHarshEventDailyLogs" },
  );
};

/** GetFilteredDriverHarshEventsSummary */
export const useFilteredDriverHarshEventsSummary = (
  params: HarshDrivingReportsFilteredSummaryHookParams,
): BaseHookReturn<DriverHarshEventsTotalDtoPagedResultDto> => {
  return useApiRequest<DriverHarshEventsTotalDtoPagedResultDto>(
    [
      params.fromDate,
      params.toDate,
      params.periodType,
      params.skipCount,
      params.maxResultCount,
    ],
    async (axios, baseURL) => {
      const request: ApiAppHarsheventreportGetfiltereddriverharsheventssummaryPostRequest = {
        fromDate: params.fromDate.toISOString(),
        toDate: params.toDate.toISOString(),
        periodType: params.periodType,
        skipCount: params.skipCount,
        maxResultCount: params.maxResultCount,
      };
      const response = await HarshEventReportApiFactory(
        undefined,
        baseURL,
        axios,
      ).apiAppHarsheventreportGetfiltereddriverharsheventssummaryPost(request);
      return response.data;
    },
    { logLabel: "useFilteredDriverHarshEventsSummary" },
  );
};
